import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ServiceItem } from "@/types";
import { Gift, Loader2, User, Mail, Phone } from "lucide-react";
import { useGiftCardAvailableServices } from "@/hooks/useGiftCardAvailableServices";
import { formatPrice, formatDuration } from "@/utils/serviceFormatters";
import { formatPhoneNumber, cleanPhoneNumber, validatePhoneNumber } from "@/utils/phoneUtils";
import { calculateAmountWithStripeFees } from "@/utils/stripeFeesCalculator";
import { Separator } from "@/components/ui/separator";
import { AlertCircle } from "lucide-react";

interface GiftCardFormProps {
  onSubmit: (e: React.FormEvent) => void;
  onChange: (formData: any) => void;
  formData: {
    // Sender info
    senderFirstName: string;
    senderLastName: string;
    senderEmail: string;
    senderPhone: string;
    // Recipient info
    recipientFirstName: string;
    recipientLastName: string;
    recipientEmail: string;
    recipientPhone: string;
    // Gift details
    message: string;
    selectedServices: string[];
    customAmount: string;
    giftType: "service" | "amount";
  };
  isLoading: boolean;
}

const GiftCardForm = ({ onSubmit, onChange, formData, isLoading }: GiftCardFormProps) => {
  const { data: availableServices, isLoading: isServicesLoading } = useGiftCardAvailableServices();
  const [categorizedServices, setCategorizedServices] = useState<Record<string, ServiceItem[]>>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [senderPhoneDisplay, setSenderPhoneDisplay] = useState(formData.senderPhone || "");
  const [recipientPhoneDisplay, setRecipientPhoneDisplay] = useState(formData.recipientPhone || "");
  
  useEffect(() => {
    if (availableServices && availableServices.length > 0) {
      const categories: Record<string, ServiceItem[]> = {};
      
      availableServices.forEach(availableService => {
        const service = availableService.services;
        if (!categories[service.category_id]) {
          categories[service.category_id] = [];
        }
        categories[service.category_id].push(service);
      });
      
      setCategorizedServices(categories);
    }
  }, [availableServices]);
  
  const handleFieldChange = useCallback((field: string, value: any) => {
    onChange({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  }, [formData, onChange, errors]);

  const handlePhoneChange = (field: "senderPhone" | "recipientPhone", value: string) => {
    const cleaned = cleanPhoneNumber(value);
    
    if (cleaned.length <= 10) {
      const formatted = formatPhoneNumber(cleaned);
      if (field === "senderPhone") {
        setSenderPhoneDisplay(formatted);
      } else {
        setRecipientPhoneDisplay(formatted);
      }
      handleFieldChange(field, cleaned);
      
      if (cleaned.length === 10 && !validatePhoneNumber(cleaned)) {
        setErrors(prev => ({ ...prev, [field]: "Numéro de téléphone invalide" }));
      } else if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: "" }));
      }
    }
  };

  const handleEmailBlur = (field: string, value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value && !emailRegex.test(value)) {
      setErrors(prev => ({ ...prev, [field]: "Adresse email invalide" }));
    }
  };

  const handleNameChange = (field: string, value: string) => {
    const cleaned = value.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, "");
    const capitalized = cleaned
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    handleFieldChange(field, capitalized);
  };
  
  const handleGiftTypeChange = (value: "service" | "amount") => {
    console.log("🔄 RadioGroup - Changement de type:", value);
    console.log("📊 État actuel:", formData.giftType);
    
    if (value === "amount") {
      onChange({
        ...formData,
        giftType: value,
        selectedServices: [],
        customAmount: formData.customAmount || ""
      });
      console.log("✅ Mode montant activé");
    } else {
      onChange({
        ...formData,
        giftType: value,
        customAmount: "",
        selectedServices: formData.selectedServices || []
      });
      console.log("✅ Mode service activé");
    }
  };
  
  const handleServiceToggle = (serviceId: string) => {
    const currentSelected = [...formData.selectedServices];
    
    if (currentSelected.includes(serviceId)) {
      handleFieldChange("selectedServices", currentSelected.filter(id => id !== serviceId));
    } else {
      handleFieldChange("selectedServices", [...currentSelected, serviceId]);
    }
  };

  const handleAmountChange = (value: string) => {
    const cleaned = value.replace(/[^\d.,]/g, "").replace(",", ".");
    const numValue = parseFloat(cleaned);
    
    if (cleaned === "" || (!isNaN(numValue) && numValue <= 1000)) {
      handleFieldChange("customAmount", cleaned);
      
      if (numValue && numValue < 10) {
        setErrors(prev => ({ ...prev, customAmount: "Le montant minimum est de 10€" }));
      } else if (errors.customAmount) {
        setErrors(prev => ({ ...prev, customAmount: "" }));
      }
    }
  };

  const messageLength = formData.message?.length || 0;
  const maxMessageLength = 500;

  // Calcul du montant total avec frais Stripe
  const calculateTotalAmount = () => {
    let baseAmount = 0;
    
    if (formData.giftType === "amount" && formData.customAmount) {
      const amount = parseFloat(formData.customAmount);
      if (!isNaN(amount) && amount >= 10) {
        baseAmount = amount;
      }
    } else if (formData.giftType === "service" && formData.selectedServices.length > 0) {
      formData.selectedServices.forEach(serviceId => {
        const service = availableServices?.find(s => s.service_id === serviceId)?.services;
        if (service) {
          const priceMatch = service.price.match(/(\d+)[,.]?(\d*)/);
          const price = priceMatch ? parseFloat(`${priceMatch[1]}.${priceMatch[2] || 0}`) : 0;
          baseAmount += price;
        }
      });
    }
    
    return baseAmount > 0 ? calculateAmountWithStripeFees(baseAmount) : null;
  };

  const amountBreakdown = calculateTotalAmount();

  const isFormValid = () => {
    const hasErrors = Object.values(errors).some(error => error !== "");
    const requiredFields = [
      formData.senderFirstName,
      formData.senderLastName,
      formData.senderEmail,
      formData.senderPhone,
      formData.recipientFirstName,
      formData.recipientLastName,
      formData.recipientEmail,
    ];
    
    const allFieldsFilled = requiredFields.every(field => field && field.trim() !== "");
    
    if (formData.giftType === "service") {
      return allFieldsFilled && !hasErrors && formData.selectedServices.length > 0;
    } else {
      const amount = parseFloat(formData.customAmount);
      return allFieldsFilled && !hasErrors && amount >= 10;
    }
  };

  // Vérifie si le récapitulatif de paiement doit être affiché
  const shouldShowPaymentSummary = () => {
    // Vérifier que toutes les infos du sender sont remplies
    const senderInfoComplete = 
      formData.senderFirstName?.trim() !== "" &&
      formData.senderLastName?.trim() !== "" &&
      formData.senderEmail?.trim() !== "" &&
      formData.senderPhone?.trim() !== "" &&
      !errors.senderEmail &&
      !errors.senderPhone;
    
    // Vérifier que toutes les infos du recipient sont remplies
    const recipientInfoComplete = 
      formData.recipientFirstName?.trim() !== "" &&
      formData.recipientLastName?.trim() !== "" &&
      formData.recipientEmail?.trim() !== "" &&
      !errors.recipientEmail;
    
    // Vérifier qu'un type de cadeau valide est sélectionné
    const giftTypeValid = 
      (formData.giftType === "service" && formData.selectedServices.length > 0) ||
      (formData.giftType === "amount" && parseFloat(formData.customAmount) >= 10);
    
    return senderInfoComplete && recipientInfoComplete && giftTypeValid && amountBreakdown !== null;
  };

  return (
    <Card className="bg-white shadow-md">
      <CardHeader>
        <CardTitle className="flex items-center justify-center gap-2 font-playfair text-2xl">
          <Gift className="h-6 w-6" />
          Demander un bon cadeau
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Vos informations */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="font-medium text-lg mb-4 text-bbz-green-dark">Vos informations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="senderFirstName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Votre prénom <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="senderFirstName"
                  type="text"
                  autoComplete="given-name"
                  autoCapitalize="words"
                  maxLength={50}
                  value={formData.senderFirstName}
                  onChange={(e) => handleNameChange("senderFirstName", e.target.value)}
                  placeholder="Marie"
                  required
                  aria-required="true"
                  className={errors.senderFirstName ? "border-red-500" : ""}
                />
                {errors.senderFirstName && (
                  <p className="text-sm text-red-600">{errors.senderFirstName}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="senderLastName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Votre nom <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="senderLastName"
                  type="text"
                  autoComplete="family-name"
                  autoCapitalize="words"
                  maxLength={50}
                  value={formData.senderLastName}
                  onChange={(e) => handleNameChange("senderLastName", e.target.value)}
                  placeholder="Dupont"
                  required
                  aria-required="true"
                  className={errors.senderLastName ? "border-red-500" : ""}
                />
                {errors.senderLastName && (
                  <p className="text-sm text-red-600">{errors.senderLastName}</p>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="senderEmail" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Votre email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="senderEmail"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  maxLength={255}
                  value={formData.senderEmail}
                  onChange={(e) => handleFieldChange("senderEmail", e.target.value.toLowerCase())}
                  onBlur={(e) => handleEmailBlur("senderEmail", e.target.value)}
                  placeholder="marie.dupont@exemple.fr"
                  required
                  aria-required="true"
                  className={errors.senderEmail ? "border-red-500" : ""}
                />
                {errors.senderEmail && (
                  <p className="text-sm text-red-600">{errors.senderEmail}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="senderPhone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Votre téléphone <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="senderPhone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  value={senderPhoneDisplay}
                  onChange={(e) => handlePhoneChange("senderPhone", e.target.value)}
                  placeholder="06 12 34 56 78"
                  required
                  aria-required="true"
                  aria-invalid={!!errors.senderPhone}
                  className={errors.senderPhone ? "border-red-500" : ""}
                />
                {errors.senderPhone && (
                  <p className="text-sm text-red-600">{errors.senderPhone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Informations du bénéficiaire */}
          <div className="border-b border-gray-200 pb-6">
            <h3 className="font-medium text-lg mb-4 text-bbz-green-dark">Informations du bénéficiaire</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="recipientFirstName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Prénom du bénéficiaire <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="recipientFirstName"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="words"
                  maxLength={50}
                  value={formData.recipientFirstName}
                  onChange={(e) => handleNameChange("recipientFirstName", e.target.value)}
                  placeholder="Sophie"
                  required
                  aria-required="true"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipientLastName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Nom du bénéficiaire <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="recipientLastName"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="words"
                  maxLength={50}
                  value={formData.recipientLastName}
                  onChange={(e) => handleNameChange("recipientLastName", e.target.value)}
                  placeholder="Martin"
                  required
                  aria-required="true"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="recipientEmail" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email du bénéficiaire <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="recipientEmail"
                  type="email"
                  autoComplete="off"
                  inputMode="email"
                  maxLength={255}
                  value={formData.recipientEmail}
                  onChange={(e) => handleFieldChange("recipientEmail", e.target.value.toLowerCase())}
                  onBlur={(e) => handleEmailBlur("recipientEmail", e.target.value)}
                  placeholder="sophie.martin@exemple.fr"
                  required
                  aria-required="true"
                  className={errors.recipientEmail ? "border-red-500" : ""}
                />
                {errors.recipientEmail && (
                  <p className="text-sm text-red-600">{errors.recipientEmail}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipientPhone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Téléphone du bénéficiaire <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="recipientPhone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="off"
                  value={recipientPhoneDisplay}
                  onChange={(e) => handlePhoneChange("recipientPhone", e.target.value)}
                  placeholder="06 98 76 54 32"
                  required
                  aria-required="true"
                  aria-invalid={!!errors.recipientPhone}
                  className={errors.recipientPhone ? "border-red-500" : ""}
                />
                {errors.recipientPhone && (
                  <p className="text-sm text-red-600">{errors.recipientPhone}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Type de bon cadeau */}
          <div>
            <h3 className="font-medium text-lg mb-3 text-bbz-green-dark">Type de bon cadeau*</h3>
            
            <RadioGroup 
              value={formData.giftType} 
              onValueChange={(value) => handleGiftTypeChange(value as "service" | "amount")}
              className="space-y-3"
            >
              <div className="flex items-center space-x-2 cursor-pointer">
                <RadioGroupItem value="service" id="gift-service" />
                <Label htmlFor="gift-service" className="cursor-pointer select-none">Choisir un ou plusieurs services spécifiques</Label>
              </div>
              
              <div className="flex items-center space-x-2 cursor-pointer">
                <RadioGroupItem value="amount" id="gift-amount" />
                <Label htmlFor="gift-amount" className="cursor-pointer select-none">Offrir un montant au choix</Label>
              </div>
            </RadioGroup>
            
            {formData.giftType === "amount" && (
              <div className="mt-4 space-y-2">
                <Label htmlFor="customAmount" className="flex items-center gap-2">
                  Montant (€) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="customAmount"
                  type="text"
                  inputMode="decimal"
                  value={formData.customAmount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="50.00"
                  required={formData.giftType === "amount"}
                  className={`w-32 ${errors.customAmount ? "border-red-500" : ""}`}
                />
                {errors.customAmount && (
                  <p className="text-sm text-red-600">{errors.customAmount}</p>
                )}
                <p className="text-sm text-muted-foreground">
                  Montant minimum : 10€ | Suggestions : 25€, 50€, 75€, 100€
                </p>
              </div>
            )}
            
            {formData.giftType === "service" && (
              <div className="mt-4">
                <h4 className="font-medium text-sm mb-2">Sélectionnez un ou plusieurs services*</h4>
                <div className="max-h-72 overflow-y-auto p-2 border rounded-md">
                  {isServicesLoading ? (
                    <div className="py-4 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 animate-spin text-bbz-green mr-2" />
                      <p>Chargement des services...</p>
                    </div>
                  ) : Object.keys(categorizedServices).length > 0 ? (
                    Object.entries(categorizedServices).map(([categoryId, categoryServices], index) => (
                      <div key={categoryId} className={index > 0 ? "mt-4 pt-3 border-t" : ""}>
                        {categoryServices.map((service) => (
                          <div key={service.id} className="flex items-center space-x-2 py-1.5">
                            <Checkbox
                              id={`service-${service.id}`}
                              checked={formData.selectedServices.includes(service.id)}
                              onCheckedChange={() => handleServiceToggle(service.id)}
                            />
                            <label
                              htmlFor={`service-${service.id}`}
                              className="text-sm flex-1 cursor-pointer"
                            >
                              <span className="font-medium">{service.name}</span>
                              <span className="text-bbz-text/70 ml-2">
                                ({formatDuration(service.duration)} - {formatPrice(service.price)})
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500 italic py-2 text-center">Aucun service disponible</p>
                  )}
                </div>
                {formData.giftType === "service" && formData.selectedServices.length === 0 && (
                  <p className="text-sm text-red-600 mt-1">Veuillez sélectionner au moins un service</p>
                )}
              </div>
            )}
          </div>
          
          
          <div className="text-sm text-gray-500 italic">* Champs obligatoires</div>
          
          {/* Récapitulatif des frais */}
          {shouldShowPaymentSummary() && (
            <div className="mt-4 p-4 bg-bbz-cream/30 rounded-lg border border-bbz-green/20">
              <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Récapitulatif du paiement
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Valeur du bon cadeau</span>
                  <span className="font-medium">{amountBreakdown.baseAmount.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-bbz-text/70">
                  <span>Frais de traitement (Stripe)</span>
                  <span>{amountBreakdown.stripeFees.toFixed(2)} €</span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between font-semibold text-base">
                  <span>Total à payer</span>
                  <span className="text-bbz-green">{amountBreakdown.totalAmount.toFixed(2)} €</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3 italic">
                ℹ️ Les frais de traitement bancaire sont ajoutés au montant du bon cadeau. 
                Vous recevrez un bon cadeau d'une valeur de {amountBreakdown.baseAmount.toFixed(2)} €, 
                utilisable dans son intégralité.
              </p>
            </div>
          )}
          
          <Button 
            type="submit" 
            className="w-full"
            disabled={isLoading || !isFormValid()}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Traitement en cours...
              </>
            ) : (
              <>
                <Gift className="w-4 h-4 mr-2" />
                Procéder au paiement
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default GiftCardForm;
